/**
 * Captures errors from the HttpAgent and surfaces them in the UI.
 *
 * Two error paths exist:
 * 1. **RUN_ERROR AG-UI events** — errors sent as SSE events within the stream.
 *    The @assistant-ui/react-ag-ui subscriber does not handle onRunErrorEvent,
 *    so these would be silently dropped without this hook.
 * 2. **HTTP-level errors** (e.g. 409 "message already in progress") — the
 *    request fails before any SSE stream starts. These surface through the
 *    `onRunFailed` subscriber callback.
 *
 * Cancellation-related errors are suppressed: aborting the SSE stream causes
 * the fetch to fail asynchronously with various error types (AbortError,
 * BodyStreamBuffer errors, etc). The `suppressRef` ensures that once
 * `clearError()` is called (at the start of cancel), all subsequent errors
 * are ignored until the next run starts.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import { runErrorMessage } from "./runErrorMessage";

interface UseRunErrorReturn {
  error: string | null;
  /** Call before cancelling to suppress any errors from the dying stream. */
  clearError: () => void;
}

export function useRunError(agent: HttpAgent): UseRunErrorReturn {
  const [error, setError] = useState<string | null>(null);
  // Suppresses all errors after clearError() until the next run starts.
  // Aborting a stream can surface errors asynchronously through multiple
  // callbacks (onRunFailed, onRunErrorEvent) with unpredictable error types,
  // so type-based filtering alone isn't reliable.
  const suppressRef = useRef(false);

  useEffect(() => {
    const sub = agent.subscribe({
      onRunInitialized: () => {
        setError(null);
        suppressRef.current = false;
      },
      onRunErrorEvent: ({ event }: { event: { message?: string } }) => {
        if (suppressRef.current) {
          return;
        }
        // Ignore cancellation errors — backend sends these when user cancels
        if (event.message?.toLowerCase().includes("cancelled")) {
          return;
        }
        setError(event.message ?? "An unexpected error occurred");
      },
      onRunFailed: ({ error: runError }: { error: unknown }) => {
        if (suppressRef.current) {
          return;
        }
        // Ignore abort errors — these come from cancel and are expected
        if (runError instanceof DOMException && runError.name === "AbortError") {
          return;
        }
        console.error("Run failed", runError);
        setError(runErrorMessage(runError));
      },
    });

    return () => {
      sub.unsubscribe();
    };
  }, [agent]);

  const clearError = useCallback(() => {
    suppressRef.current = true;
    setError(null);
  }, []);

  return { error, clearError };
}
